import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const API = process.env.REACT_APP_API_URL;

function fmtEur(centimes) {
  return ((centimes || 0) / 100).toFixed(2) + " €";
}

export default function AffiliateDashboard() {
  const navigate = useNavigate();
  const token = localStorage.getItem("affiliateToken");
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (!token) navigate("/affilie/login");
  }, [token, navigate]);

  const fetchAll = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [meRes, statsRes, salesRes] = await Promise.all([
        axios.get(`${API}/api/affiliates/me`, { headers }),
        axios.get(`${API}/api/affiliates/me/stats`, { headers }),
        axios.get(`${API}/api/affiliates/me/sales`, { headers }),
      ]);
      setMe(meRes.data);
      setStats(statsRes.data);
      setSales(salesRes.data.sales);
      setForm({
        contactName: meRes.data.contactName || "",
        email: meRes.data.email || "",
        phone: meRes.data.phone || "",
      });
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("affiliateToken");
        navigate("/affilie/login");
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleLogout = () => {
    localStorage.removeItem("affiliateToken");
    navigate("/affilie/login");
  };

  const handleSave = async () => {
    setSaveError(null);
    setSaveSuccess(false);
    try {
      await axios.patch(`${API}/api/affiliates/me`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSaveSuccess(true);
      setEditing(false);
      fetchAll();
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err?.response?.data?.message || "Erreur de sauvegarde");
    }
  };

  if (loading || !me || !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100 text-stone-600">
        Chargement…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 p-4 md:p-8">
      <div className="max-w-5xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex justify-between items-start flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-stone-800">{me.name}</h1>
            <p className="text-sm text-stone-600 mt-1">
              Code partenaire :{" "}
              <span className="font-mono bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                {me.couponCode}
              </span>
              {" · "}
              <span className="text-stone-500">
                Taux : {(me.commissionRate * 100).toFixed(0)}%
              </span>
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-stone-200 hover:bg-stone-300 rounded-lg text-sm font-medium transition"
          >
            Déconnexion
          </button>
        </div>

        {/* Stats principales */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            label="Ventes totales"
            value={stats.totalSales}
            sub={`${stats.salesThisMonth} ce mois`}
          />
          <StatCard
            label="Commission totale"
            value={fmtEur(stats.totalCommission)}
            sub={`${fmtEur(stats.monthCommission)} ce mois`}
            highlight="emerald"
          />
          <StatCard
            label="Reste à recevoir"
            value={fmtEur(stats.unpaidCommission)}
            sub={`${fmtEur(stats.paidCommission)} déjà reçu`}
            highlight="amber"
          />
        </div>

        {/* Mes informations */}
        <Card
          title="📋 Mes informations"
          action={
            !editing ? (
              <button
                onClick={() => setEditing(true)}
                className="px-3 py-1 bg-orange-600 hover:bg-orange-500 text-white rounded-lg text-xs font-medium transition"
              >
                Modifier
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setEditing(false);
                    setSaveError(null);
                    setForm({
                      contactName: me.contactName || "",
                      email: me.email || "",
                      phone: me.phone || "",
                    });
                  }}
                  className="px-3 py-1 bg-stone-200 hover:bg-stone-300 rounded-lg text-xs font-medium transition"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  className="px-3 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-medium transition"
                >
                  Sauvegarder
                </button>
              </div>
            )
          }
        >
          {saveSuccess && (
            <div className="mb-3 p-2 rounded bg-emerald-50 border border-emerald-200 text-sm text-emerald-700">
              ✅ Informations mises à jour
            </div>
          )}
          {saveError && (
            <div className="mb-3 p-2 rounded bg-red-50 border border-red-200 text-sm text-red-700">
              {saveError}
            </div>
          )}
          {editing ? (
            <div className="space-y-3">
              <Field
                label="Nom du contact"
                value={form.contactName}
                onChange={(v) => setForm({ ...form, contactName: v })}
              />
              <Field
                label="Email"
                type="email"
                value={form.email}
                onChange={(v) => setForm({ ...form, email: v })}
              />
              <Field
                label="Téléphone"
                value={form.phone}
                onChange={(v) => setForm({ ...form, phone: v })}
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Row label="Type" value={typeLabel(me.type)} />
              <Row label="Nom du contact" value={me.contactName} />
              <Row label="Email" value={me.email} />
              <Row label="Téléphone" value={me.phone} />
              <Row label="Compte créé" value={format(new Date(me.createdAt), "dd MMM yyyy", { locale: fr })} />
            </div>
          )}
        </Card>

        {/* Liste des ventes */}
        <Card title={`💸 Mes ventes (${sales.length})`}>
          {sales.length === 0 ? (
            <div className="text-center py-8 text-stone-500">
              <p className="text-sm">Aucune vente pour le moment.</p>
              <p className="text-xs mt-2">
                Partagez votre code <span className="font-mono">{me.couponCode}</span> à vos clients pour commencer à toucher des commissions.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-stone-500 uppercase text-xs">
                  <tr>
                    <th className="py-2 px-3 text-left">Date</th>
                    <th className="py-2 px-3 text-left">Pack</th>
                    <th className="py-2 px-3 text-right">Montant</th>
                    <th className="py-2 px-3 text-right">Commission</th>
                    <th className="py-2 px-3 text-left">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((s) => (
                    <tr key={s.id} className="border-t border-stone-200">
                      <td className="py-3 px-3 text-stone-700">
                        {format(new Date(s.createdAt), "dd MMM yyyy", { locale: fr })}
                      </td>
                      <td className="py-3 px-3 text-stone-600">
                        {s.sessions} × {s.packType}
                      </td>
                      <td className="py-3 px-3 text-right text-stone-700">
                        {fmtEur(s.amountPaid)}
                      </td>
                      <td className="py-3 px-3 text-right font-semibold text-emerald-700">
                        {fmtEur(s.commission)}
                      </td>
                      <td className="py-3 px-3">
                        {s.commissionPaidAt ? (
                          <span className="inline-flex items-center text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                            ✅ Reçu le {format(new Date(s.commissionPaidAt), "dd/MM", { locale: fr })}
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                            En attente
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div className="text-center text-xs text-stone-400 py-4">
          Blackout Games — Espace partenaire
        </div>
      </div>
    </div>
  );
}

function typeLabel(t) {
  const labels = {
    escape_room: "🔐 Escape Room",
    boutique_jeu: "🎲 Boutique de jeux",
    other: "📦 Autre",
  };
  return labels[t] || t;
}

function Card({ title, children, action }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
        <h3 className="text-base font-semibold text-stone-800">{title}</h3>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function StatCard({ label, value, sub, highlight }) {
  const colors = {
    emerald: "border-emerald-200 bg-emerald-50",
    amber: "border-amber-200 bg-amber-50",
    default: "border-stone-200 bg-white",
  };
  const valueColors = {
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    default: "text-stone-800",
  };
  return (
    <div className={`rounded-2xl p-5 border ${colors[highlight] || colors.default}`}>
      <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">{label}</p>
      <p className={`text-2xl font-bold mt-2 ${valueColors[highlight] || valueColors.default}`}>
        {value}
      </p>
      {sub && <p className="text-xs text-stone-500 mt-1">{sub}</p>}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between py-2 border-b border-stone-100 last:border-0">
      <span className="text-sm text-stone-500">{label}</span>
      <span className="text-sm text-stone-800">{value || "—"}</span>
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="block text-xs font-medium text-stone-500 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:outline-none focus:ring-2 focus:ring-orange-500"
      />
    </div>
  );
}